
import { useState, useCallback, useEffect } from 'react';
import { Exercise, ActivityType } from '@/models/LearnTypes';
import { useExerciseCore } from './exercise/useExerciseCore';
import { useExerciseProgress } from './exercise/useExerciseProgress';
import { useFlashCardManager } from './useFlashCardManager';
import { useSpellingExercise } from './exercise/useSpellingExercise';
import { useLevelProgressionMutations } from './mutations/useLevelProgressionMutations';
import { useExerciseNavigation } from './exercise/useExerciseNavigation';
import { useRecyclingPhase } from './exercise/useRecyclingPhase';
import { useAnswerChecker } from './exercise/useAnswerChecker';
import { useExerciseUIState } from './exercise/useExerciseUIState';

interface UseExerciseStateProps {
  exercises: Exercise[];
  setExercises: React.Dispatch<React.SetStateAction<Exercise[]>>;
  userId?: string;
  selectedLesson?: any | null;
  onImperfect?: () => void; // Add callback for marking level as imperfect
}

interface UseExerciseStateReturn {
  currentExerciseIndex: number;
  showAnswer: boolean;
  userInput: string;
  isInputCorrect: boolean | null;
  wordsToReview: string[];
  totalOriginalExercises: number;
  
  setCurrentExerciseIndex: React.Dispatch<React.SetStateAction<number>>;
  setShowAnswer: React.Dispatch<React.SetStateAction<boolean>>;
  setUserInput: (input: string) => void;
  setIsInputCorrect: React.Dispatch<React.SetStateAction<boolean | null>>;
  setWordsToReview: React.Dispatch<React.SetStateAction<string[]>>;
  setTotalOriginalExercises: React.Dispatch<React.SetStateAction<number>>;
  
  handleCheckAnswer: () => void;
  handleNextExercise: (onComplete: (wordsToReview: string[]) => void) => void;
  handlePrevExercise: () => void;
  
  currentExercise: Exercise | undefined;
  progress: number;
  isLastExercise: boolean;
  totalExercises: number;
  correctlyAnsweredWords: string[];
  displayedExerciseNumber: number;
  
  onLogActivity: (type: ActivityType, exercise: Exercise) => void;
}

export const useExerciseState = ({
  exercises,
  setExercises,
  userId,
  selectedLesson,
  onImperfect // Add new parameter for marking level as imperfect
}: UseExerciseStateProps): UseExerciseStateReturn => {
  // Initialize the activity logging mutation
  const mutations = useLevelProgressionMutations({ userId, selectedLesson });
  
  // State for tracking correctly answered words
  const [correctlyAnsweredWords, setCorrectlyAnsweredWords] = useState<string[]>([]);
  
  // Core exercise state management
  const {
    currentExerciseIndex,
    showAnswer,
    userInput,
    isInputCorrect,
    wordsToReview,
    totalOriginalExercises,
    
    setCurrentExerciseIndex,
    setShowAnswer,
    setUserInput,
    setIsInputCorrect,
    setWordsToReview,
    setTotalOriginalExercises,
    
    currentExercise
  } = useExerciseCore({
    exercises,
    setExercises
  });
  
  // Set up a dummy next handler for the FlashCardManager
  const handleDummyNext = () => {};
  
  // FlashCard-specific state management
  const {
    isFlipped,
    setIsFlipped,
    manuallyFlippedCards,
    wasManuallyFlipped,
    checkForManualFlip,
    resetFlipState
  } = useFlashCardManager({
    currentExercise: currentExercise || { id: '', type: 'flashcard', word: '', translation: '' },
    onNext: handleDummyNext,
    showAnswer
  });
  
  // UI state management
  const { resetUIState } = useExerciseUIState({
    setShowAnswer,
    setUserInput,
    setIsInputCorrect,
    resetFlipState
  });
  
  // Recycling phase detection
  const { isInRecyclingPhase } = useRecyclingPhase({
    exercises,
    currentExerciseIndex,
    totalOriginalExercises,
    wordsToReview,
    currentExerciseId: currentExercise?.id
  });
  
  // Spelling exercise management
  const {
    handleCheckSpellingAnswer
  } = useSpellingExercise({
    exercises,
    setExercises,
    wordsToReview,
    setWordsToReview,
    correctlyAnsweredWords,
    setCorrectlyAnsweredWords,
    setShowAnswer,
    currentExercise,
    userInput,
    setIsInputCorrect
  });
  
  // Answer checking
  const { handleCheckAnswer } = useAnswerChecker({
    currentExercise,
    setShowAnswer,
    handleCheckSpellingAnswer
  });
  
  // Progress tracking
  const {
    isLastExercise,
    progress,
    totalExercises,
    displayedExerciseNumber,
    isRecycledWord
  } = useExerciseProgress({
    exercises,
    currentExerciseIndex,
    totalOriginalExercises,
    wordsToReview,
    currentExerciseId: currentExercise?.id
  });
  
  // Navigation between exercises
  const { handleNextExercise, handlePrevExercise } = useExerciseNavigation({
    currentExercise,
    currentExerciseIndex,
    exercises,
    wordsToReview,
    correctlyAnsweredWords,
    wasManuallyFlipped,
    isLastExercise,
    
    setCurrentExerciseIndex,
    setWordsToReview,
    setCorrectlyAnsweredWords,
    resetUIState,
    
    // Pass the onImperfect callback for perfect level tracking
    onImperfect
  });
  
  // Activity logging functionality
  const logActivity = useCallback((type: ActivityType, exercise: Exercise) => {
    if (userId && selectedLesson) {
      console.log(`🔄 [logActivity] Logging activity: ${type} for word: ${exercise.word}`);
      mutations.activityMutation.mutate({
        userId: userId,
        type: type,
        lessonId: selectedLesson.id,
        lessonName: selectedLesson.name,
        wordId: exercise.id,
        word: exercise.word,
        xpEarned: 0
      });
    } else {
      console.warn('Cannot log activity: missing userId or selectedLesson');
    }
  }, [userId, selectedLesson, mutations.activityMutation]);
  
  // Log word review state for debugging
  useEffect(() => {
    console.log(`🔄 [useExerciseState] Words to review updated:`, wordsToReview);
  }, [wordsToReview]);
  
  // Log exercise state changes for debugging
  useEffect(() => {
    if (exercises.length > 0) {
      console.log(`🔄 [useExerciseState] Current exercise: ${currentExerciseIndex}/${exercises.length}, totalOriginal: ${totalOriginalExercises}`);
      if (currentExercise) {
        console.log(`🔄 [useExerciseState] Current word: ${currentExercise.word}, id: ${currentExercise.id}`);
      }
    }
  }, [exercises, currentExerciseIndex, totalOriginalExercises, currentExercise]);
  
  return {
    currentExerciseIndex,
    showAnswer,
    userInput,
    isInputCorrect,
    wordsToReview,
    totalOriginalExercises,
    
    setCurrentExerciseIndex,
    setShowAnswer,
    setUserInput,
    setIsInputCorrect,
    setWordsToReview,
    setTotalOriginalExercises,
    
    handleCheckAnswer,
    handleNextExercise,
    handlePrevExercise,
    
    currentExercise,
    progress,
    isLastExercise,
    totalExercises,
    correctlyAnsweredWords,
    displayedExerciseNumber,
    
    onLogActivity: logActivity
  };
};
